/* istanbul ignore file */
/* TODO: Refactor directory structure */
/* FIXME: Add unit tests */
import {
  datadogLogs,
  type LogsEvent,
  type Logger,
  type LogsInitConfiguration,
} from '@datadog/browser-logs';
import log from 'loglevel';

const LOG_LEVELS = [
  'trace',
  'debug',
  'info',
  'warn',
  'error',
  'silent',
] as const;

export type LogLevel = (typeof LOG_LEVELS)[number];

export function isLogLevelType(value?: string): value is LogLevel {
  return LOG_LEVELS.includes(value as LogLevel);
}

export type LogConfig = {
  /**
   * Whether DataDog is enabled.
   */
  datadogEnabled: boolean;

  /**
   * A Datadog client token.
   *
   * https://docs.datadoghq.com/account_management/api-app-keys/#client-tokens
   */
  datadogClientToken?: string;

  /**
   * The Datadog site parameter of your organization.
   *
   * https://docs.datadoghq.com/getting_started/site/
   */
  datadogSite?: string;

  /**
   * The Datadog forwardErrorsToLogs parameter.
   *
   * https://docs.datadoghq.com/logs/log_collection/javascript/#setup
   */
  datadogForwardErrorsToLogs?: boolean;

  /**
   * The Datadog forwardConsoleLogs parameter.
   *
   * https://docs.datadoghq.com/logs/log_collection/javascript/#setup
   */
  datadogForwardConsoleLogs?: LogsInitConfiguration['forwardConsoleLogs'];

  /**
   * The service name for your application.
   *
   * Must follow the tag syntax requirements:
   * https://docs.datadoghq.com/getting_started/tagging/#define-tags
   */
  service: string;

  /**
   * The application’s environment, for example: prod, pre-prod, staging, etc.
   *
   * Must follow the tag syntax requirements:
   * https://docs.datadoghq.com/getting_started/tagging/#define-tags
   */
  env: string;

  /**
   * The application’s version, for example: 1.2.3, 6c44da20, 2020.02.13, etc.
   *
   * Must follow the tag syntax requirements:
   * https://docs.datadoghq.com/getting_started/tagging/#define-tags
   */
  version: string;

  /**
   * The initially, globally, enabled log level.
   *
   * This does not effect which logs are sent to DataDog, if enabled.
   */
  logLevel: LogLevel;

  /**
   * Disables default logs engine (console.log) if values is true DataDog enabled.
   *
   * Main case - would like to disable console logs for production when DataDog enabled
   */
  isDefaultDisabled?: boolean;
};

/* We're saving the log config for later. */
let logConfig: LogConfig | undefined = undefined;

/**
 * Returns the logging configuration.
 *
 * @returns The log config passed to `initLogger`.
 */
export function getLogConfig(): LogConfig {
  if (logConfig === undefined) {
    throw new Error('logging is not initialized. call `initLogger` first.');
  }

  return logConfig;
}

/**
 * Get the logger for \b moduleName.
 *
 * @param moduleName - The module name for the logger.
 * @returns A new logger.
 * @note This method should only be used by a file that instantiates a logger.
 */
export function _getLogger(moduleName: string, context?: object): log.Logger {
  if (logConfig === undefined) {
    console.error(
      'logging is not initialized. call `initLogger` first. exiting.',
    );
    return log.getLogger(moduleName);
  }

  if (logConfig.datadogEnabled) {
    datadogLogs.createLogger(moduleName, {
      context,
    });
  }

  return log.getLogger(moduleName);
}

export type ContextValue = unknown;
export type Context = Record<string, ContextValue>;

/* We're saving the global logger context. */
let globalContext: Context = {};

/**
 * Set global context
 * @param newContext - new logger global context
 */
export function setLoggerContext(newContext: Context): void {
  if (logConfig === undefined) {
    throw new Error('logging is not initialized. call `initLogger` first.');
  }

  globalContext = newContext;

  if (logConfig.datadogEnabled) {
    // Context type is not exported from dd, so we cast to any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    datadogLogs.setGlobalContext(globalContext as any);
  }
}

/**
 * Clear global context
 */
export function clearLoggerContext(): void {
  if (logConfig === undefined) {
    throw new Error('logging is not initialized. call `initLogger` first.');
  }

  globalContext = {};

  if (logConfig.datadogEnabled) {
    datadogLogs.clearGlobalContext();
  }
}

/**
 * Get global context
 * @returns global logger context.
 */
export function getLoggerContext(): Context {
  if (logConfig === undefined) {
    throw new Error('logging is not initialized. call `initLogger` first.');
  }

  if (logConfig.datadogEnabled) {
    return datadogLogs.getGlobalContext();
  }

  return globalContext;
}

/**
 * Update global context
 * @param newContext - new props to logger global context
 */
export function updateLoggerContext(newContext: Context): void {
  setLoggerContext({ ...getLoggerContext(), ...newContext });
}

type LogFunction = (
  message: string,
  messageContext?: object,
  error?: Error,
) => void;

function getDataDogLogFunction(
  logMethod: string,
  loggerName?: string | symbol,
): LogFunction | null {
  const logger = ((): Logger => {
    if (loggerName === undefined) {
      return datadogLogs.logger;
    }

    const namedLogger =
      typeof loggerName === 'string'
        ? datadogLogs.getLogger(loggerName)
        : datadogLogs.getLogger(loggerName.toString());
    if (namedLogger !== undefined) {
      return namedLogger;
    }

    return datadogLogs.logger;
  })();

  switch (logMethod) {
    case 'trace':
    case 'debug': {
      return (message, messageContext, error) =>
        logger.debug(message, messageContext, error);
    }
    case 'log':
    case 'info': {
      return (message, messageContext, error) =>
        logger.info(message, messageContext, error);
    }
    case 'warn': {
      return (message, messageContext, error) =>
        logger.warn(message, messageContext, error);
    }
    case 'error': {
      return (message, messageContext, error) =>
        logger.error(message, messageContext, error);
    }
    default: {
      log.error('unknown log function:', logMethod);
      return null;
    }
  }
}

/**
 * Converts the arguments into strings, stripping out all object arguments.
 *
 * @param ...args - Arguments to the logging function.
 * @returns A string, concatenated from the arguments, joined by a space.
 */
function getDataDogMessageString(args: unknown[]): string {
  return args
    .map((arg) => {
      // Include error message(s) in the log's message string.
      if (arg instanceof Error) {
        return arg.message;
      }
      return arg;
    })
    .filter((arg) => {
      // Undefined, Null, Function, and Symbol explicitly ignored.
      return (
        typeof arg === 'string' ||
        typeof arg === 'number' ||
        typeof arg === 'boolean' ||
        typeof arg === 'bigint'
      );
    })
    .join(' ');
}

/**
 * Strips out all arguments which are not objects.
 *
 * @param ...args - Arguments to the logging function.
 * @returns A single context object, extracted from `args`, including logger name.
 */
function getDataDogContext(
  loggerName: string | symbol,
  args: unknown[],
): object {
  const objectArgs = args.filter((arg) => {
    return arg !== null && typeof arg === 'object' && !(arg instanceof Error);
  });

  return Object.assign({}, ...objectArgs, { name: loggerName }) as object;
}

/**
 * Find the argument which is an Error.
 *
 * @param ...args - Arguments to the logging function.
 * @returns An Error object if present.
 */
function getDataDogErrorArg(args: unknown[]): Error | undefined {
  return args.find((arg): arg is Error => arg instanceof Error);
}

/**
 * Initialize the log level library.
 */
function initLogLevel(config: LogConfig): void {
  const originalFactory = log.methodFactory;
  log.methodFactory = (
    methodName: string,
    logLevel: log.LogLevelNumbers,

    // Despite claiming this is only 'string | symbol', it can be undefined.
    loggerName: string | symbol = '?',
  ): log.LoggingMethod => {
    // `rawMethod` is the original `console.{trace,log,info,warn,error}`.
    const rawMethod = originalFactory(methodName, logLevel, loggerName);

    // We're explictly ignoring logLevel here.
    const datadogLog = getDataDogLogFunction(methodName, loggerName);
    const logLevelString = methodName.toUpperCase();

    const loggerNameString: string = loggerName.toString();

    return function (...args: unknown[]) {
      // Forward the logs to DataDog.
      if (config.datadogEnabled && datadogLog) {
        const messageString = getDataDogMessageString(args);
        const contextObject = getDataDogContext(loggerName, args);
        const errorArg = getDataDogErrorArg(args);
        datadogLog(messageString, contextObject, errorArg);

        if (config.isDefaultDisabled === true) {
          return;
        }
      }

      const copy = [...args];
      const now = new Date();
      const timestamp = now
        .toTimeString()
        .replace(/.*(\d{2}:\d{2}:\d{2}).*/u, '$1');
      const prefix = `[${timestamp}] [${logLevelString}] ${loggerNameString}:`;
      copy.unshift(prefix);
      copy.push(globalContext);

      rawMethod(...copy);
    };
  };

  log.setLevel(config.logLevel); // Be sure to call setLevel method in order to apply plugin
}

/**
 * Role value used when the session is a patient (matches RealtimeRole.Patient).
 * Used to strip network/IP from logs for PII/PHI compliance.
 */
const PATIENT_ROLE = 'Patient';

/**
 * Removes network and client IP from the log event when the role is Patient (PII/PHI).
 * DataDog merges global context (including role) into the event; network may be in
 * event.network, event.attributes.network, or event.context.network.
 * Exported for unit testing.
 */
export function stripNetworkFromEventIfPatient(event: LogsEvent): void {
  const { role, attributes, context } = event as unknown as {
    role?: string;
    attributes?: Record<string, unknown>;
    context?: Record<string, unknown>;
  };
  const effectiveRole = role ?? attributes?.['role'] ?? context?.['role'];
  if (effectiveRole !== PATIENT_ROLE) {
    return;
  }
  delete (event as Record<string, unknown>)['network'];
  delete attributes?.['network'];
  delete context?.['network'];
}

/**
 * Initialize datadog logging library.
 */
function initDataDog(config: LogConfig): void {
  if (!config.datadogEnabled || config.datadogClientToken === undefined) {
    return;
  }

  datadogLogs.init({
    clientToken: config.datadogClientToken,
    site: config.datadogSite,
    service: config.service,
    env: config.env,
    version: config.version,
    forwardErrorsToLogs: config.datadogForwardErrorsToLogs ?? false,
    forwardConsoleLogs: config.datadogForwardConsoleLogs ?? [],
    sampleRate: 100,
    silentMultipleInit: false,
    telemetrySampleRate: 0,
    beforeSend: (event: LogsEvent): boolean => {
      if (event.http && event.http.status_code === 404) {
        return false;
      }

      stripNetworkFromEventIfPatient(event);
      return true;
    },
  });
}

const isNode = (): boolean =>
  typeof process !== 'undefined' &&
  process.versions !== null &&
  process.versions.node !== null;

function initUncaughtExceptionHandler(): void {
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event): void => {
      log.error('uncaught exception:', event.error, {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });
  } else if (isNode()) {
    process.on('uncaughtException', (err) => {
      log.error('uncaught exception:', err);
    });
  } else {
    log.error('unknown execution environment');
  }
}

/**
 * Initialize logging subsystem.
 *
 * @param config - The configuration for the DataDog logger.
 */
export function initLogger(config: LogConfig): void {
  if (logConfig !== undefined) {
    console.warn('logging is already initialized');
  }

  logConfig = config;
  initDataDog(config);
  initLogLevel(config);
  initUncaughtExceptionHandler();
}
